import { Upload } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { FieldError } from './field-error'
import { linkVariants } from './Link'
import { Spinner } from './spinner'

interface DropzoneProps {
  /** Extra content rendered inside the box, below the built-in copy. */
  children?: React.ReactNode
  /** Callback when a single file is selected */
  onChange?: (file: File | null) => void
  /** Callback when multiple files are selected (requires multiple=true) */
  onMultipleChange?: (files: File[]) => void
  /** Allow selecting multiple files */
  multiple?: boolean
  /** Accept specific file types (e.g., ".csv,.pdf" or "image/*") */
  accept?: string
  /** Maximum file size in MB */
  maxSizeMB?: number
  /** Hides the size/format hint line without lifting the limit itself. */
  showFileHints?: boolean
  /** Inverts the inner copy for a dropzone sitting on an ink-800/900 ground. */
  onDark?: boolean
  /** Optional className for the wrapper */
  className?: string
  /** Disable the dropzone */
  disabled?: boolean
  /** Is loading */
  isLoading?: boolean
  maxWidth?: number
}

/**
 * Dropzone component with drag-and-drop support.
 *
 * @example
 * ```tsx
 * const [file, setFile] = useState<File | null>(null)
 *
 * <Dropzone
 *   onChange={setFile}
 *   accept=".csv,.pdf"
 *   maxSizeMB={10}
 * />
 * ```
 */
/** ".pdf,.csv" -> "pdf, csv" — exported so callers can show the same list the dropzone does. */
function formatAcceptedFormats(accept: string): string {
  return accept
    .split(',')
    .map((ext) => ext.trim().replace('.', ''))
    .join(', ')
}

function Dropzone({
  onChange,
  onMultipleChange,
  multiple = false,
  accept = '.csv,.pdf',
  maxSizeMB = 10,
  showFileHints = true,
  onDark = false,
  className,
  disabled = false,
  isLoading = false,
  maxWidth = 488,
  children,
}: DropzoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = React.useState(false)
  const [file, setFile] = React.useState<File | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const acceptedFormats = formatAcceptedFormats(accept)

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  /** Dropping a file bypasses the input's own `accept` filter, so the check lives here too. */
  const matchesAccept = (file: File): boolean => {
    const patterns = accept
      .split(',')
      .map((pattern) => pattern.trim().toLowerCase())
      .filter(Boolean)
    if (patterns.length === 0) return true
    const name = file.name.toLowerCase()
    const type = file.type.toLowerCase()
    return patterns.some((pattern) => {
      if (pattern.startsWith('.')) return name.endsWith(pattern)
      if (pattern.endsWith('/*')) return type.startsWith(pattern.slice(0, -1))
      return type === pattern
    })
  }

  /** Why the file was turned away, or null when it's fine. */
  const rejectionReason = (file: File): string | null => {
    if (!matchesAccept(file)) return `${file.name} isn’t an accepted format — ${acceptedFormats} only`
    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      return `${file.name} is over the ${maxSizeMB} MB limit`
    }
    return null
  }

  const handleFilesSelect = (fileList: FileList) => {
    // A single-file dropzone takes the first of a multi-file drop, as its input would.
    const selected = multiple ? Array.from(fileList) : Array.from(fileList).slice(0, 1)
    const valid = selected.filter((file) => rejectionReason(file) === null)

    setError(selected.map(rejectionReason).find((reason) => reason !== null) ?? null)

    if (multiple) {
      onMultipleChange?.(valid)
      return
    }
    // A rejected file clears the last good one too — a stale name beside a disabled submit
    // button reads as a working selection.
    setFile(valid[0] ?? null)
    onChange?.(valid[0] ?? null)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) {
      setError(null)
      setFile(null)
      onChange?.(null)
      return
    }
    handleFilesSelect(files)
    // Without this the same file picked twice in a row fires no change event at all.
    event.target.value = ''
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!disabled) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)

    if (disabled) return

    const files = event.dataTransfer.files
    if (files.length > 0) {
      handleFilesSelect(files)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.key === 'Enter' || event.key === ' ') && !disabled) {
      event.preventDefault()
      inputRef.current?.click()
    }
  }

  function renderContent() {
    if (isLoading) {
      return (
        <div className="flex w-full items-center justify-center gap-2">
          <Spinner className="size-5" />
        </div>
      )
    }

    if (isDragOver) {
      return (
        <div className="flex w-full items-center justify-center gap-2">
          <Upload className={cn('size-5', onDark ? 'text-ink-200' : 'text-ink-600')} />
          <span className={cn('text-sm font-medium', onDark ? 'text-ink-200' : 'text-ink-600')}>
            Drop file to upload
          </span>
        </div>
      )
    }
    return (
      <div className="flex w-full flex-col items-center justify-center gap-3">
        <div className="flex items-center gap-2">
          <Upload className={cn('size-5', onDark ? 'text-ink-300' : 'text-ink-400')} />
          <span className={cn('text-sm font-medium', onDark ? 'text-ink-200' : 'text-ink-600')}>
            Drag and drop or <span className={linkVariants({ kind: 'nav', onDark })}>browse</span>
          </span>
        </div>
        {error ? (
          <div role="alert" className="text-center break-words">
            <FieldError message={error} onDark={onDark} />
          </div>
        ) : file ? (
          <span className="text-sm font-medium break-all">{file.name}</span>
        ) : (
          showFileHints && (
            <div className="text-center text-xs text-gray-500">
              <span className="pr-2">Max. size: {maxSizeMB} MB</span>
              <span>Accepted file: {acceptedFormats}</span>
            </div>
          )
        )}
        {children}
      </div>
    )
  }

  return (
    <div
      style={{ maxWidth }}
      className={cn(
        'relative mx-auto flex w-full cursor-pointer items-center justify-center rounded-sm border border-dashed p-6 transition-colors',
        isDragOver
          ? 'border-blue-500 bg-blue-50'
          : cn('bg-brand-50 border-gray-300', !disabled && 'hover:border-gray-400 hover:bg-gray-50'),
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label="Upload file"
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        disabled={disabled}
        className="hidden"
        aria-hidden="true"
      />

      {renderContent()}
    </div>
  )
}

export { Dropzone, formatAcceptedFormats }
export type { DropzoneProps }
